/* eslint-disable complexity, max-lines */
import { IconNames } from '@blueprintjs/icons';
import { datadogLogs } from '@datadog/browser-logs';
import { skipToken } from '@reduxjs/toolkit/query';
import { DateFormats, formatDate } from '@strata/helper-functions';
import { CollapsibleSection } from '@strata/react-collapsible-section';
import { FormSection } from '@strata/react-form-section';
import { Grid, ProductCell, SecondaryData } from '@strata/react-grid';
import { InlineMessage } from '@strata/react-inline-message';
import { MessagingBlock } from '@strata/react-messaging-block';
import { InformationIcon } from '@strata/react-status-icons';
import {
    OpportunityStatusIndicator,
    TreatmentStatusIndicator,
} from '@strata/react-status-indicators';
import classNames from 'classnames';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { useFlags } from 'launchdarkly-react-client-sdk';
import * as PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { IntakeCaseDetails } from './IntakeCaseDetails';

import { OpportunityDetailsOverview } from 'components/opportunities/OpportunityDetailsOverview';
import { ReferralAndOnboardingStatusIndicator } from 'components/referrals/ReferralAndOnboardingStatusIndicator';
import { ReferralDetailsOverview } from 'components/referrals/ReferralDetailsOverview';
import { AuthDetails } from 'components/shared/AuthDetails';
import { TreatmentMemberDetails } from 'components/treatments/TreatmentMemberDetails';
import {
    buildIntakeCaseMilestoneReferralFieldsFromTreatment,
    getCurrentReferralId,
} from 'services/intakeCaseHelpers';
import {
    computeWillBeUnmanagedOnDate,
    PLATFORM_ELIGIBILITY_PENDING,
} from 'services/memberEligibilityStatus';
import { AppRoutes } from 'services/routes';
import { useGetIntakeCasesForTreatmentQuery } from 'store/api/intakeCaseApi';
import {
    useGetAllProductServicesQuery,
    useGetAllSuppliersQuery,
    useGetAllTherapyGroupsQuery,
} from 'store/api/referenceApi';
import { useGetBenefitCheckConfigQuery } from 'store/api/scopeAssessorApi';
import { useGetCohortPeerTreatmentsQuery } from 'store/api/treatmentApi';

dayjs.extend(isBetween);

const COHORT_PEER_PRODUCT_COLUMN_WIDTH = 300;
const COHORT_PEER_TREATMENT_STATUS_COLUMN_WIDTH = 200;

const initialSortProperties = [{ id: 'receivedTime', desc: true }];

const cohortPeerTreatmentColumns = ({ productServiceMap }) => [
    {
        accessorKey: 'productServiceOid',
        header: 'Product',
        size: COHORT_PEER_PRODUCT_COLUMN_WIDTH,
        cell: ({ row }) => (
            <ProductCell
                product={productServiceMap?.[row.original.productServiceOid]}
                isBrand={row.original.isBrand}
            />
        ),
    },
    {
        accessorKey: 'treatmentStatus',
        header: 'Treatment Status',
        size: COHORT_PEER_TREATMENT_STATUS_COLUMN_WIDTH,
        cell: ({ getValue }) => <TreatmentStatusIndicator status={getValue()} />,
    },
];

const columns = ({ supplierMap, enrollmentStatus }) => [
    {
        accessorKey: 'friendlyId',
        header: 'Referral ID',
        size: 80,
    },
    {
        accessorKey: 'servicingSupplierNpi',
        header: 'Servicing Supplier',
        size: 80,
        cell: ({ getValue }) => <SecondaryData data={supplierMap?.[getValue()]?.name} />,
    },
    {
        accessorKey: 'routeName',
        header: 'Route Rule',
        size: 80,
    },
    {
        accessorKey: 'benefitType',
        header: 'Benefit',
        size: 80,
    },
    {
        accessorKey: 'receivedTime',
        header: 'Received Date',
        size: 80,
        cell: ({ getValue }) => (
            <SecondaryData data={formatDate(getValue(), DateFormats.DATE_TIME)} />
        ),
    },
    {
        accessorKey: 'referralStatus',
        header: 'Referral Status',
        size: 80,
        cell: ({ getValue, row }) =>
            row.original.opportunityStatus ? (
                <OpportunityStatusIndicator status={row.original.opportunityStatus} />
            ) : (
                <ReferralAndOnboardingStatusIndicator
                    referralStatus={getValue()}
                    referralId={row.original.referralId}
                    enrollmentStatus={enrollmentStatus}
                />
            ),
    },
];

export const TreatmentDetailOverview = ({
    opportunityDetails = undefined,
    opportunityFetching,
    treatmentDetails = undefined,
    treatmentFetching,
    memberDetails,
    currentLocation,
}) => {
    const { showIntakeTimelineAgora, agoraEnhancedEligibility } = useFlags();
    const navigate = useNavigate();
    const { data: intakeCases, isFetching: intakeCasesFetching } =
        useGetIntakeCasesForTreatmentQuery(
            treatmentDetails
                ? {
                      memberId: treatmentDetails.memberDetails?.memberId,
                      memberFirstName: treatmentDetails.memberDetails?.firstName,
                      memberDOB: treatmentDetails.memberDetails?.dateOfBirth,
                      segmentId: treatmentDetails.segmentId,
                      productServiceOid: treatmentDetails.productServiceOid,
                  }
                : skipToken,
        );
    const { data: allSuppliersData } = useGetAllSuppliersQuery();
    const { supplierMap } = allSuppliersData ?? {};
    const { data: allProductServicesData } = useGetAllProductServicesQuery();
    const { productServiceMap } = allProductServicesData ?? {};
    const { data: allTherapyGroupsData } = useGetAllTherapyGroupsQuery();
    const { therapyGroupMap } = allTherapyGroupsData ?? {};

    const { referralList, authList } = treatmentDetails ?? {
        referralList: [],
        authList: [],
    };

    const { data: cohortPeerTreatments, isFetching: cohortPeerTreatmentsFetching } =
        useGetCohortPeerTreatmentsQuery(
            treatmentDetails?.payerId && treatmentDetails?.treatmentId
                ? {
                      payerId: treatmentDetails.payerId,
                      treatmentId: treatmentDetails.treatmentId,
                  }
                : skipToken,
        );

    const cohortPeerTreatmentsExcludingCurrent = useMemo(
        () =>
            cohortPeerTreatments?.treatments.filter(
                (treatment) => treatment.treatmentId !== treatmentDetails?.treatmentId,
            ) ?? [],
        [cohortPeerTreatments, treatmentDetails?.treatmentId],
    );

    const therapyGroupDescription =
        therapyGroupMap?.[cohortPeerTreatments?.therapyGroupName] ??
        cohortPeerTreatments?.therapyGroupName;

    const nonOpportunityReferralList = referralList?.filter(
        (referral) => referral.referralId !== opportunityDetails?.referralId,
    );

    const mostCurrentReferralId = getCurrentReferralId(referralList);
    const mostCurrentReferralRow = referralList?.find(
        (referral) => referral.referralId === mostCurrentReferralId,
    );

    const activeChild = {
        referralId: undefined,
        authId: undefined,
        sectionTitle: undefined,
        DetailsOverview: undefined,
    };
    if (opportunityDetails?.referralId) {
        activeChild.referralId = opportunityDetails?.referralId;
        activeChild.authId = opportunityDetails?.authId;
        activeChild.sectionTitle = 'Active Opportunity Overview';
        activeChild.DetailsOverview = OpportunityDetailsOverview;
    } else if (mostCurrentReferralId) {
        activeChild.referralId = mostCurrentReferralId;
        activeChild.authId = mostCurrentReferralRow?.authId;
        activeChild.sectionTitle = 'Active Referral Overview';
        activeChild.DetailsOverview = ReferralDetailsOverview;
    }

    const intakeMilestoneReferralId = opportunityDetails?.referralId ?? mostCurrentReferralId;

    const intakeCaseMilestoneReferralFields = useMemo(
        () =>
            buildIntakeCaseMilestoneReferralFieldsFromTreatment(
                treatmentDetails,
                intakeMilestoneReferralId,
            ),
        [treatmentDetails, intakeMilestoneReferralId],
    );

    const treatmentPayerId = treatmentDetails?.payerId;
    const treatmentSegmentId = treatmentDetails?.segmentId;
    const memberIdForUnmanagedDate = memberDetails?.memberId;
    const memberEnrollmentStatus = memberDetails?.enrollmentStatus;
    const memberPendingAt = memberDetails?.pendingAt;

    const shouldFetchWillBeUnmanagedOn =
        agoraEnhancedEligibility &&
        !!treatmentPayerId &&
        !!treatmentSegmentId &&
        !!memberIdForUnmanagedDate &&
        memberEnrollmentStatus === PLATFORM_ELIGIBILITY_PENDING;

    const { data: delayedUnmanagementDays } = useGetBenefitCheckConfigQuery(
        shouldFetchWillBeUnmanagedOn
            ? { payerId: treatmentPayerId, segmentId: treatmentSegmentId }
            : skipToken,
    );

    const willBeUnmanagedOnForDisplay = useMemo(() => {
        if (!shouldFetchWillBeUnmanagedOn || !delayedUnmanagementDays) {
            return undefined;
        }
        return computeWillBeUnmanagedOnDate(memberPendingAt, delayedUnmanagementDays) ?? undefined;
    }, [shouldFetchWillBeUnmanagedOn, delayedUnmanagementDays, memberPendingAt]);

    useEffect(() => {
        const showReferralOverviewPanel =
            !opportunityDetails?.referralId && mostCurrentReferralId != null;
        if (!showReferralOverviewPanel) {
            return;
        }
        datadogLogs.logger.info('intake_milestones_treatment_overview_panel', {
            referral_id: mostCurrentReferralId,
            payer_id: treatmentDetails?.payerId ?? '',
            ld_show_intake_timeline: String(showIntakeTimelineAgora),
            intake_fields_present: String(intakeCaseMilestoneReferralFields != null),
            treatment_fetching: String(treatmentFetching),
            timestamp_in_seconds: (dayjs().valueOf() / 1000).toString(),
        });
    }, [
        opportunityDetails?.referralId,
        mostCurrentReferralId,
        treatmentDetails?.payerId,
        showIntakeTimelineAgora,
        intakeCaseMilestoneReferralFields,
        treatmentFetching,
    ]);

    return (
        <>
            <CollapsibleSection
                title="Treatment Overview"
                className="sds_treatment__active-section-header"
                content={
                    <div className="sds_details-page-overview">
                        <div
                            className="sds_details-page-overview__container"
                            style={{ flex: '2 0' }}
                        >
                            <FormSection
                                sectionTitle="Referrals and Opportunities"
                                className="sds_treatment__grid"
                                content={
                                    <Grid
                                        enableSorting
                                        data={[
                                            ...nonOpportunityReferralList,
                                            ...(opportunityDetails ? [opportunityDetails] : []),
                                        ]}
                                        isLoading={treatmentFetching || opportunityFetching}
                                        sortProperties={initialSortProperties}
                                        noResults={
                                            <MessagingBlock
                                                size={MessagingBlock.Sizes.SMALL}
                                                icon={IconNames.CLIPBOARD}
                                                primaryText="No referrals or opportunities"
                                            />
                                        }
                                        onRowClick={(row) => {
                                            if (row.referralStatus) {
                                                navigate(
                                                    AppRoutes.referralDetails(
                                                        treatmentDetails.payerId,
                                                        row.referralId,
                                                    ),
                                                    {
                                                        state: {
                                                            backToDestination: currentLocation,
                                                        },
                                                    },
                                                );
                                            } else if (row.opportunityStatus) {
                                                navigate(
                                                    AppRoutes.opportunityDetails(row.referralId),
                                                    {
                                                        state: {
                                                            backToDestination: currentLocation,
                                                        },
                                                    },
                                                );
                                            }
                                        }}
                                        rowCssClass={(row) =>
                                            classNames('sds_treatment__grid__row', {
                                                'sds_treatment__grid__row--active':
                                                    row.referralId === activeChild.referralId,
                                            })
                                        }
                                        columns={columns({
                                            supplierMap,
                                            enrollmentStatus: memberDetails?.enrollmentStatus,
                                        })}
                                    />
                                }
                            />
                            <FormSection
                                sectionTitle="Authorizations"
                                content={
                                    <>
                                        {authList?.length > 0 &&
                                            !authList?.some((auth) =>
                                                dayjs().isBetween(
                                                    auth.authStart,
                                                    auth.authExpiration,
                                                    'day',
                                                    '[]',
                                                ),
                                            ) && (
                                                <div className="padding__bottom--medium">
                                                    <InlineMessage
                                                        label="No active authorizations for this treatment."
                                                        intent={InlineMessage.Intent.INFO}
                                                    />
                                                </div>
                                            )}
                                        <AuthDetails
                                            auths={authList}
                                            isContingentTherapy={
                                                treatmentDetails?.isContingentTherapy
                                            }
                                            isLoading={!authList}
                                        />
                                    </>
                                }
                            />
                            <FormSection
                                sectionTitle="Intake Cases"
                                content={
                                    <IntakeCaseDetails
                                        cases={intakeCases}
                                        isLoading={intakeCasesFetching}
                                    />
                                }
                            />
                            {cohortPeerTreatmentsExcludingCurrent.length > 0 && (
                                <FormSection
                                    sectionTitle={
                                        <div>
                                            Biosimilars and Related Treatments
                                            <InformationIcon
                                                icon={IconNames.INFO_SIGN}
                                                className="padding__left--small"
                                                size={16}
                                                tooltipText={`Therapy Group: ${therapyGroupDescription}`}
                                            />
                                        </div>
                                    }
                                    content={
                                        <div
                                            style={{
                                                width:
                                                    COHORT_PEER_PRODUCT_COLUMN_WIDTH +
                                                    COHORT_PEER_TREATMENT_STATUS_COLUMN_WIDTH,
                                            }}
                                        >
                                            <Grid
                                                enableSorting
                                                data={cohortPeerTreatmentsExcludingCurrent}
                                                isLoading={cohortPeerTreatmentsFetching}
                                                sortProperties={[
                                                    { id: 'productServiceOid', desc: false },
                                                ]}
                                                noResults={
                                                    <MessagingBlock
                                                        size={MessagingBlock.Sizes.SMALL}
                                                        icon={IconNames.PRESCRIPTION}
                                                        primaryText="No related treatments for this member and therapy group"
                                                    />
                                                }
                                                columns={cohortPeerTreatmentColumns({
                                                    productServiceMap,
                                                })}
                                                onRowClick={(row) =>
                                                    navigate(
                                                        AppRoutes.treatmentDetails(
                                                            treatmentDetails.payerId,
                                                            row.treatmentId,
                                                        ),
                                                        {
                                                            state: {
                                                                backToDestination: currentLocation,
                                                            },
                                                        },
                                                    )
                                                }
                                            />
                                        </div>
                                    }
                                />
                            )}
                        </div>
                        <div
                            className="sds_details-page-overview__container"
                            style={{ flex: '1 0' }}
                        >
                            <TreatmentMemberDetails
                                payerId={treatmentDetails?.payerId}
                                memberDetails={memberDetails}
                                willBeUnmanagedOn={willBeUnmanagedOnForDisplay}
                            />
                        </div>
                    </div>
                }
            />
            {activeChild.DetailsOverview && (
                <CollapsibleSection
                    title={activeChild.sectionTitle}
                    renderExpanded
                    className={classNames(
                        'padding__top--x-large',
                        'sds_treatment__active-section-header',
                    )}
                    content={
                        activeChild.DetailsOverview === ReferralDetailsOverview ? (
                            <ReferralDetailsOverview
                                payerId={treatmentDetails?.payerId}
                                referralId={activeChild.referralId}
                                intakeCaseMilestoneReferralFields={
                                    intakeCaseMilestoneReferralFields
                                }
                            />
                        ) : (
                            <OpportunityDetailsOverview
                                referralId={activeChild.referralId}
                                intakeCaseMilestoneReferralFields={
                                    intakeCaseMilestoneReferralFields
                                }
                            />
                        )
                    }
                />
            )}
        </>
    );
};

TreatmentDetailOverview.propTypes = {
    treatmentDetails: PropTypes.shape({
        treatmentId: PropTypes.string,
        segmentId: PropTypes.string,
        payerId: PropTypes.string,
        productServiceOid: PropTypes.string,
        memberDetails: PropTypes.shape({
            firstName: PropTypes.string,
            lastName: PropTypes.string,
            memberId: PropTypes.string,
            dateOfBirth: PropTypes.string,
            gender: PropTypes.string,
        }),
        referralList: PropTypes.arrayOf(
            PropTypes.shape({
                referralId: PropTypes.string,
                authId: PropTypes.string,
                servicingSupplierNpi: PropTypes.string,
                routeName: PropTypes.string,
                benefitType: PropTypes.string,
                receivedTime: PropTypes.string,
                referralStatus: PropTypes.string,
            }),
        ),
        authList: PropTypes.arrayOf(
            PropTypes.shape({
                authId: PropTypes.string,
                authStatus: PropTypes.string,
                authType: PropTypes.string,
                benefitType: PropTypes.string,
                receivedTime: PropTypes.string,
            }),
        ),
        activeReferral: PropTypes.shape({
            referralId: PropTypes.string,
            authId: PropTypes.string,
        }),
    }),
    opportunityDetails: PropTypes.shape({
        referralId: PropTypes.string,
        authId: PropTypes.string,
        servicingSupplierNpi: PropTypes.string,
        routeName: PropTypes.string,
        benefitType: PropTypes.string,
        receivedTime: PropTypes.string,
        referralStatus: PropTypes.string,
        opportunityStatus: PropTypes.string,
    }),
    memberDetails: PropTypes.shape({
        memberId: PropTypes.string,
        gender: PropTypes.string,
        dateOfBirth: PropTypes.string,
        memberAddress1: PropTypes.string,
        memberAddress2: PropTypes.string,
        memberCity: PropTypes.string,
        memberState: PropTypes.string,
        memberZip: PropTypes.string,
        benefitGroup: PropTypes.string,
        phone: PropTypes.string,
        email: PropTypes.string,
        enrollmentStatus: PropTypes.string,
        pendingAt: PropTypes.string,
        lastUpdated: PropTypes.string,
    }).isRequired,
    treatmentFetching: PropTypes.bool.isRequired,
    opportunityFetching: PropTypes.bool.isRequired,
    currentLocation: PropTypes.shape({
        text: PropTypes.string,
    }).isRequired,
};
